import React, {useContext, useState} from "react";
import {Box, Fade, IconButton, InputBase, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Cancel01Icon, Search01Icon} from "@hugeicons/core-free-icons";
import {SideMenuStyleContext} from "./sideMenuStyle";
import {Context as AppContext} from "../../../../context/AppContext";
import {RAIL, rgba} from "./rail/railTokens";
import {FlowBlock} from "./blocks/FlowBlock";
import {IntentBlock} from "./blocks/IntentBlock";
import {EntityBlock} from "./blocks/EntityBlock";
import {SkillsBlock} from "./blocks/SkillsBlock";
import {WatchdogsBlock} from "./blocks/WatchdogsBlock";
import {ConstsBlock} from "./blocks/ConstsBlock";
import {TagsBlock} from "./blocks/TagsBlock";

export {ListBlock} from "./blocks/ListBlock";

// variant: "rail" (legacy flow editor) or "panel" (registry projects: white list panel with one shared search)
// headerAction: optional element on the right of the panel title row (registry projects: the language picker)
export const SideMenu = ({flows, selectedFlow, project, onSelectFlow, variant = "rail", headerAction = null}) => {
    const {updateProject, getUserProjects} = useContext(AppContext);
    const [search, setSearch] = useState("");

    const shared = {project, onUpdateProject: updateProject, getUserProjects};

    const blocks = (
        <>
            <FlowBlock {...shared} flow={selectedFlow} onSelectFlow={onSelectFlow}/>
            <IntentBlock {...shared} flow={selectedFlow}/>
            <EntityBlock {...shared} flow={selectedFlow}/>
            <SkillsBlock {...shared}/>
            <WatchdogsBlock {...shared}/>
            <ConstsBlock {...shared}/>
            <TagsBlock {...shared}/>
        </>
    );

    if (variant === "panel") {
        return (
            <SideMenuStyleContext.Provider value={{variant, search}}>
                <Stack
                    data-tour="flow-sidebar"
                    sx={{
                        width: {md: 300, xl: 320}, flexShrink: 0, minHeight: 0, bgcolor: "#fff",
                        border: "1px solid #ECECF3", borderRadius: "18px", overflow: "hidden",
                        boxShadow: "0 1px 2px rgba(35, 38, 77, 0.04)",
                    }}
                >
                    <Box sx={{px: 2, pt: 2.25, pb: 1.25}}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{mb: 0.5}}>
                            <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", color: "#8491F7"}}>DESIGNER</Typography>
                            {headerAction}
                        </Stack>
                        <Typography noWrap sx={{fontSize: 18, fontWeight: 700, color: "#23264D", letterSpacing: "-0.01em"}}>
                            {selectedFlow?.name || "Flows"}
                        </Typography>
                        <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>
                            {(flows || []).length} flows · {selectedFlow?.nodes?.length || 0} nodes in this flow
                        </Typography>
                        <Box component="label" sx={{
                            mt: 1.5, display: "flex", alignItems: "center", gap: 1, height: 36, px: 1.25, borderRadius: "10px",
                            border: "1px solid #E4E4EE", color: "#6B6B80", "&:focus-within": {borderColor: "#8491F7"},
                        }}>
                            <HugeiconsIcon icon={Search01Icon} size={15}/>
                            <InputBase
                                placeholder="Search flows, intents, entities..."
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                onKeyDown={(event) => event.key === "Escape" && setSearch("")}
                                sx={{flex: 1, fontSize: 13, color: "#23264D"}}
                            />
                            {search && (
                                <IconButton size="small" sx={{p: 0.25}} onClick={() => setSearch("")}>
                                    <HugeiconsIcon icon={Cancel01Icon} size={13}/>
                                </IconButton>
                            )}
                        </Box>
                    </Box>
                    <Box sx={{flex: 1, minHeight: 0, overflowY: "auto", pb: 1}}>
                        {blocks}
                    </Box>
                </Stack>
            </SideMenuStyleContext.Provider>
        );
    }

    return (
        <Fade in={!!project} timeout={500}>
            <Stack
                data-tour="flow-sidebar"
                direction="column"
                sx={{
                    maxHeight: "100%",
                    maxWidth: 280,
                    width: "100%",
                    py: 0.5,
                    backgroundColor: RAIL.surface,
                    borderRight: `1px solid ${rgba(RAIL.accent, 0.12)}`,
                    overflowY: "auto",
                }}
            >
                <FlowBlock {...shared} flow={selectedFlow} onSelectFlow={onSelectFlow}/>
                <IntentBlock {...shared} flow={selectedFlow}/>
                <EntityBlock {...shared} flow={selectedFlow}/>
                <SkillsBlock {...shared}/>
                <WatchdogsBlock {...shared}/>
                <ConstsBlock {...shared}/>
                <TagsBlock {...shared}/>
            </Stack>
        </Fade>
    );
};
